import type { z } from 'zod';

export type ApiSchemaVersion = string;

/** CASL scope pair for API route authorization. */
export type ApiRouteScope = readonly [action: string, subject: string];

export type ApiSchemaMapperDefinition = {
  readonly fromVersion: ApiSchemaVersion;
  readonly toVersion: ApiSchemaVersion;
  body?(body: unknown): unknown;
  queryParams?(query: Record<string, unknown>): Record<string, unknown>;
  routeParams?(params: Record<string, unknown>): Record<string, unknown>;
  response?(response: unknown): unknown;
};

export type ApiSchemaMapperClass = new () => ApiSchemaMapperDefinition;

export type ApiSchemaDefinition = {
  readonly name: ApiSchemaVersion;
  /** Request body Zod schema for this API version. */
  readonly schema?: z.ZodType;
  readonly query?: z.ZodType;
  /** URL path / route parameter Zod schema for this API version. */
  readonly params?: z.ZodType;
  readonly response?: z.ZodType;
  /** Mappers from older schema versions into this canonical version. */
  readonly mappers?: readonly ApiSchemaMapperClass[];
  /** CASL scope pair applied when this schema is used on a route. */
  readonly scopes?: ApiRouteScope;
};

export type ApiSchemaClassStatic = {
  mappers?(): ApiSchemaMapperClass[];
};

export type ApiSchemaClass = (new () => ApiSchemaDefinition) &
  ApiSchemaClassStatic;

export function instantiateApiSchema(
  schemaClass: ApiSchemaClass
): ApiSchemaDefinition {
  return new schemaClass();
}

export function resolveSchemaMappers(
  schemaClass: ApiSchemaClass
): ApiSchemaMapperClass[] {
  if (schemaClass.mappers !== undefined) {
    return schemaClass.mappers();
  }

  const instance = instantiateApiSchema(schemaClass);
  if (instance.mappers !== undefined) {
    return [...instance.mappers];
  }

  return [];
}
